import { Resource } from '@opentelemetry/resources';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';

// Interface untuk konfigurasi SignOz
export interface SignOzConfig {
  serviceName: string;
  serviceVersion: string;
  environment: string;
  serviceNamespace: string;
  url: string;
  headers?: Record<string, string>;
  traceSampleRate?: number;
  batchSpanProcessorConfig?: {
    maxQueueSize?: number;
    scheduledDelayMillis?: number;
    exportTimeoutMillis?: number;
  };
  allowedOrigins?: (string | RegExp)[];
}

// Interface untuk window object dengan environment variables
declare global {
  interface Window {
    REACT_APP_SIGNOZ_SERVICE_NAME: string;
    REACT_APP_SIGNOZ_SERVICE_VERSION: string;
    REACT_APP_SIGNOZ_ENV: string;
    REACT_APP_SIGNOZ_SERVICE_NAMESPACE: string;
    REACT_APP_SIGNOZ_URL: string;
    REACT_APP_SIGNOZ_TRACE_SAMPLE_RATE: string;
    REACT_APP_SIGNOZ_ALLOWED_ORIGINS: string;
  }
}

// Fungsi untuk mendapatkan nilai konfigurasi dari process.env atau window
function getConfigValue(key: string): string | undefined {
  // Cek process.env terlebih dahulu
  if (typeof process !== 'undefined' && process.env) {
    const envValue = process.env[key];
    if (envValue) return envValue;
  }

  // Fallback ke window jika process.env kosong
  if (typeof window !== 'undefined') {
    return (window as any)[key];
  }

  return undefined;
}

// Fungsi untuk memvalidasi konfigurasi
function validateConfig(config: Partial<SignOzConfig>): { isValid: boolean; missingFields: string[] } {
  const requiredFields = ['serviceName', 'serviceVersion', 'environment', 'serviceNamespace', 'url'];
  const missingFields: string[] = [];

  for (const field of requiredFields) {
    if (!config[field as keyof SignOzConfig]) {
      missingFields.push(field);
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
}

// Fungsi untuk mengkonversi string allowed origins ke array
function parseAllowedOrigins(originsStr?: string): (string | RegExp)[] {
  if (!originsStr) return [/.+/g];
  
  return originsStr.split(',').map(origin => {
    origin = origin.trim();
    // Jika string dimulai dan diakhiri dengan '/', anggap sebagai RegExp
    if (origin.startsWith('/') && origin.endsWith('/')) {
      return new RegExp(origin.slice(1, -1));
    }
    return origin;
  });
}

/**
 * Inisialisasi SignOz tracing untuk aplikasi React
 * @param config - Konfigurasi SignOz (opsional, akan menggunakan environment variables jika tidak disediakan)
 */
export function initializeSignOzTracing(config?: SignOzConfig): void {
  try {
    // Gabungkan konfigurasi dari parameter, process.env, dan window
    const effectiveConfig = {
      serviceName: config?.serviceName || getConfigValue('REACT_APP_SIGNOZ_SERVICE_NAME'),
      serviceVersion: config?.serviceVersion || getConfigValue('REACT_APP_SIGNOZ_SERVICE_VERSION'),
      environment: config?.environment || getConfigValue('REACT_APP_SIGNOZ_ENV'),
      serviceNamespace: config?.serviceNamespace || getConfigValue('REACT_APP_SIGNOZ_SERVICE_NAMESPACE'),
      url: config?.url || getConfigValue('REACT_APP_SIGNOZ_URL'),
      headers: config?.headers || {},
      traceSampleRate: (() => {
        const rate = config?.traceSampleRate || parseFloat(String(getConfigValue('REACT_APP_SIGNOZ_TRACE_SAMPLE_RATE'))) || 1.0;
        return Math.max(0, Math.min(1, rate)); // Ensure rate is between 0 and 1
      })(),
      batchSpanProcessorConfig: config?.batchSpanProcessorConfig || {
        maxQueueSize: 100,
        scheduledDelayMillis: 5000,
        exportTimeoutMillis: 30000
      },
      allowedOrigins: config?.allowedOrigins || parseAllowedOrigins(getConfigValue('REACT_APP_SIGNOZ_ALLOWED_ORIGINS'))
    };

    // Validasi konfigurasi
    const { isValid, missingFields } = validateConfig(effectiveConfig);

    if (!isValid) {
      console.warn(`SignOz: Konfigurasi tidak lengkap. Field yang hilang: ${missingFields.join(', ')}`);
      console.warn('SignOz: Tracing dinonaktifkan karena konfigurasi tidak lengkap');
      return;
    }

    // Define resource and service attributes
    const resource = new Resource({
      'service.name': effectiveConfig.serviceName,
      'service.version': effectiveConfig.serviceVersion,
      'deployment.environment': effectiveConfig.environment,
      'service.namespace': effectiveConfig.serviceNamespace,
    });

    // Set up the OTLP trace exporter
    const exporter = new OTLPTraceExporter({
      url: effectiveConfig.url,
      headers: effectiveConfig.headers
    });

    // Set up the span processor with configuration
    const processor = new BatchSpanProcessor(exporter, {
      maxQueueSize: effectiveConfig.batchSpanProcessorConfig.maxQueueSize,
      scheduledDelayMillis: effectiveConfig.batchSpanProcessorConfig.scheduledDelayMillis,
      exportTimeoutMillis: effectiveConfig.batchSpanProcessorConfig.exportTimeoutMillis
    });

    // Create and configure the WebTracerProvider
    const provider = new WebTracerProvider({
      resource: resource,
      sampler: new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(effectiveConfig.traceSampleRate)
      })
    });

    // Add span processor to the provider
    provider.addSpanProcessor(processor);

    // Register the tracer provider with the context manager
    provider.register({
      contextManager: new ZoneContextManager(),
    });

    // Set up automatic instrumentation for web APIs
    registerInstrumentations({
      instrumentations: [
        getWebAutoInstrumentations({
          '@opentelemetry/instrumentation-xml-http-request': {
            propagateTraceHeaderCorsUrls: effectiveConfig.allowedOrigins,
          },
          '@opentelemetry/instrumentation-fetch': {
            propagateTraceHeaderCorsUrls: effectiveConfig.allowedOrigins,
          },
        }),
      ],
    });

    console.log('SignOz tracing berhasil diinisialisasi');
  } catch (error) {
    console.error('SignOz: Gagal menginisialisasi tracing:', error);
  }
}

export default initializeSignOzTracing; 